import styles from "./notificationsListModal.module.scss";
import {observer} from "mobx-react-lite";
import React, {useEffect, useRef, useState} from "react";
import moment from "moment";
import {useHistory} from "react-router-dom";
import Modal from "@components/UI/modal/modal";
import Loader from "@components/UI/loader/loader";
import Button from "@components/UI/buttons/button/button";
import useFetching from "@source/hooks/useFetching";
import NotificationController from "@source/api/controllers/notificationController";
import {DEFAULT_NOTIFICATIONS_LIMIT, IEmployeeNotification, NEW_NOTIFICATIONS_LIMIT, notificationDocumentPath} from "@source/api/models/notificationModels";
import notificationsStore, {NotificationTab} from "@store/notificationsStore";

const NotificationsListModal = observer(() => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const requestedPage = useRef(0);
  const loadedPages = useRef<Set<number>>(new Set());
  const [getNotifications, isLoading] = useFetching<ReturnType<typeof NotificationController.getNotifications>>(NotificationController.getNotifications);
  const [getUnreadCount] = useFetching<ReturnType<typeof NotificationController.getUnreadCount>>(NotificationController.getUnreadCount);

  useEffect(() => {
    if (notificationsStore.listOpen) {
      return;
    }

    setPage(0);
    requestedPage.current = 0;
    loadedPages.current = new Set();
  }, [notificationsStore.listOpen]);

  useEffect(() => {
    if (!notificationsStore.listOpen) {
      return;
    }

    let cancelled = false;

    const loadNewNotifications = async () => {
      const notifications = await getNotifications({
        status: "unread",
        page: 0,
        limit: NEW_NOTIFICATIONS_LIMIT,
      });

      if (cancelled) {
        return;
      }

      notificationsStore.setNewNotifications(Array.isArray(notifications) ? notifications : []);

      const unreadCount = await getUnreadCount();
      if (cancelled || !unreadCount) {
        return;
      }

      notificationsStore.setUnreadCount(unreadCount.count);
    };

    loadNewNotifications();

    return () => {
      cancelled = true;
    };
  }, [notificationsStore.listOpen]);

  useEffect(() => {
    if (!notificationsStore.listOpen || !notificationsStore.newLoaded || notificationsStore.tab !== "all") {
      return;
    }

    if (loadedPages.current.has(page)) {
      return;
    }

    let cancelled = false;

    const loadAllNotifications = async () => {
      const notifications = await getNotifications({
        status: "all",
        page,
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
      });

      if (cancelled) {
        return;
      }

      if (!Array.isArray(notifications)) {
        if (!page) notificationsStore.setNotifications([]);
        return;
      }

      loadedPages.current.add(page);

      if (page) {
        notificationsStore.addNotifications(notifications);
      } else {
        notificationsStore.setNotifications(notifications);
      }
    };

    loadAllNotifications();

    return () => {
      cancelled = true;
    };
  }, [notificationsStore.listOpen, notificationsStore.newLoaded, notificationsStore.tab, page]);

  const onClose = () => {
    notificationsStore.closeList();
  };

  const onTab = (tab: NotificationTab) => {
    if (!notificationsStore.newLoaded) {
      return;
    }

    notificationsStore.setTab(tab);
  };

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (notificationsStore.tab !== "all") {
      return;
    }

    const target = event.currentTarget;
    const isNearEnd = target.scrollHeight - (target.scrollTop + target.clientHeight) < 100;
    const nextPage = page + 1;

    if (isNearEnd && !isLoading && notificationsStore.hasMore && notificationsStore.allLoaded && requestedPage.current !== nextPage) {
      requestedPage.current = nextPage;
      setPage(nextPage);
    }
  };

  const onOpenDocument = (notification: IEmployeeNotification) => {
    const path = notificationDocumentPath(notification);
    if (!path) {
      return;
    }

    notificationsStore.closeList();
    history.push(path);
  };

  const items = notificationsStore.tab === "new" ? notificationsStore.newNotifications : notificationsStore.notifications;
  const waitingForList = !notificationsStore.newLoaded || (notificationsStore.tab === "all" && !notificationsStore.allLoaded && !items.length);

  return (
    <Modal open={notificationsStore.listOpen} close={onClose} className={styles.modal} maxWidth={560}>
      <div className={styles.content}>
        <span className={styles.title}>Notifications</span>
        <div className={styles.tabs} role="tablist" aria-label="Notifications">
          <button type="button" role="tab" aria-selected={notificationsStore.tab === "new"} className={notificationsStore.tab === "new" ? styles.tabActive : styles.tab} onClick={() => onTab("new")}>
            New notifications
          </button>
          <button type="button" role="tab" aria-selected={notificationsStore.tab === "all"} className={notificationsStore.tab === "all" ? styles.tabActive : styles.tab} onClick={() => onTab("all")}>
            All notifications
          </button>
        </div>

        <div className={styles.list} onScroll={onScroll}>
          {items.map((notification) => {
            const documentPath = notificationDocumentPath(notification);
            const createdAt = moment(notification.createdAt);

            return (
              <div className={styles.item} key={notification._id}>
                <div className={styles.titleRow}>
                  {notificationsStore.showsNewDot(notification) ?
                    <span className={styles.dot} title="New"><span className={styles.srOnly}>New</span></span>
                    :
                    null}
                  <span className={styles.itemTitle}>{notification.title}</span>
                </div>
                <span className={styles.body}>{notification.body}</span>
                <span className={styles.date}>
                  {createdAt.isValid() ? `${createdAt.format("DD MMM YYYY")} at ${createdAt.format("hh:mm A")}` : ""}
                </span>
                {documentPath ?
                  <Button size="small" variant="outlined" className={styles.action} onClick={() => onOpenDocument(notification)}>
                    Go to document
                  </Button>
                  :
                  null}
              </div>
            );
          })}

          {isLoading || waitingForList ?
            <Loader className={styles.loader} width={30} height={30}/>
            :
            null}

          {!isLoading && !waitingForList && !items.length ?
            <span className={styles.empty}>{notificationsStore.tab === "new" ? "No new notifications" : "No notifications"}</span>
            :
            null}
        </div>
      </div>
    </Modal>
  );
});

export default NotificationsListModal;
